import {
  AppstoreFilled,
  CrownOutlined,
  TeamOutlined,
  FacebookFilled,
  NotificationOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { Circle, Heading, Link, Tooltip } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { ConditionsIcon, XRayIcon } from '@mm/icons';
import { ReactComponent as MailFilled } from './mail-filled.svg';
import { ReactComponent as UserFilled } from 'assets/user-filled.svg';
import { campaignsOverviewCRUDActions } from '@mm/state/actions/CampaignsOverviewCRUDActions';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useEmailAccountCRUDActions } from '@mm/state/actions/EmailAccountCRUDActions';
import { PlanSubtypes } from '@mm/state/interfaces/subscription';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectEmailAnalytics } from '@mm/state/selectors/botAnalyticsSelector';
import { selectEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { enhancedPlansSelector } from '@mm/state/selectors/subscriptionSelectors';
import {
  activeChatbotIdSelector,
  activeCompanySelector,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { uniqueId } from 'lodash';
import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BookDemoButton } from '../../../../../../common/buttons/BookDemoButton/BookDemoButton';
import { useBookDemo } from '../../../../../../common/buttons/BookDemoButton/use-book-demo';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { colors } from '../../../styles/colors';
import { CrmIntegrationsTypes } from '../../Integrations/BaseIntegration/BaseIntegration';
import { StoreState } from '@mm/state/store-interfaces';
import { useInvitesCRUDActions } from '@mm/state/actions/InvitesCRUDActions';
import { selectCompanyUsers } from '@mm/state/selectors/userSelector';
import classNames from 'classnames';
import { IntegrationLinkType } from '../../Integrations/IntegrationsPanel/IntegrationsPanel';
import { useAdAccount } from '../../Integrations/NativeIntegrations/components/FacebookAdsIntegrations/use-ad-account';
import css from './ActivationBlock.module.css';
import { IntegrationIcon } from '../../Integrations/IntegrationIcon/IntegrationIcon';
import { SettingsRoutes } from '../../../../../../routes/settings-routes';
import { IN_APP_DEMO_UTM } from '../../../../../../dashboard/pages/XRayOnboarding/WebsiteReportSummary/constants';

type ActionProps = {
  icon: ReactNode;
  buttonName: string;
  buttonClassName?: string;
  onClick?: () => void;
  hide?: boolean;
  component?: ReactNode;
  disabled?: boolean;
};

const ActivationAction = ({
  icon,
  buttonName,
  buttonClassName,
  onClick,
  component,
  disabled,
  chatbotId,
}: ActionProps & { chatbotId: number }) => (
  <div
    className={css.channelContainer}
    onClick={e => {
      e.preventDefault();
    }}
  >
    {icon}
    {buttonName && (
      <Tooltip
        anchorElement={
          <PrimaryButton
            className={classNames(css.actionButton, buttonClassName)}
            color="secondary-blue"
            onClick={onClick}
            disabled={disabled}
          >
            {buttonName}
          </PrimaryButton>
        }
        css={{ container: css.tootlipContainer }}
        toolTipDisabled={!disabled}
        noDelayClose
        withoutLine
        useHover
      >
        <Link
          to={BotBuilderRoutes.xRay.getLink({
            chatbotId,
          })}
          className={css.link}
        >
          Install X-Ray Pixel
        </Link>{' '}
        <div className={css.tooltipMainText}>to proceed</div>
      </Tooltip>
    )}
    {component}
  </div>
);

export const ActivationBlock = () => {
  const company = useSelector(activeCompanySelector);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const plans = useSelector(enhancedPlansSelector);
  const invites = useSelector((state: StoreState) => state.invite.entities);
  const users = useSelector(selectCompanyUsers);
  const activeAndPendingUsers = [
    ...invites.map(invite => ({ email: invite.email, pending: true })),
    ...users.map(user => ({ email: user.email, pending: false })),
  ];
  const accounts = useSelector(selectEmailAccounts);
  const emailAnalytics = useSelector(selectEmailAnalytics);
  const hasAutomations = !!emailAnalytics.automations?.pagination?.total;

  const pagesWithAvailableSeats =
    useSelector((state: StoreState) => state.invite.pagesWithAvailableSeats) ||
    [];

  const adAccount = useAdAccount();

  useEffect(() => {
    adAccount.fetchFacebookPageAdAccounts();
  }, []);

  const currentPlanName = plans.find(
    p => p.planCode === facebookPage?.subscription?.planCode,
  )?.shortName;

  const history = useHistory();
  const { isEligibleForDemo } = useBookDemo();
  const { getAccounts } = useEmailAccountCRUDActions();
  const {
    openNewBillingModal2023,
    openActivateEmailAccountModal,
    openInviteUserModal,
    openConversionURLModal,
  } = useModalActions();
  const { retriveAddedCrms } = useConnectionCRUDActions();
  const { retrieveAll } = useInvitesCRUDActions();
  const [automationCounters, setAutomationCounters] = useState({
    total: 0,
    active: 0,
  });

  const choosePlan = () => {
    openNewBillingModal2023({
      landmineName: landmineNames.DASHBOARD_CHOOSE_PLAN_ACTION,
    });
  };

  const getAutomationCounters = async () => {
    const res = await campaignsOverviewCRUDActions.retrieveAutomationCounters(
      chatbotId,
    );
    setAutomationCounters(res);
  };

  const navigateTo = (
    kind: IntegrationLinkType,
    options?: CrmIntegrationsTypes,
  ) => {
    history.push(BotBuilderRoutes.integrations.getLink({ chatbotId }), {
      kind,
      options,
    });
  };

  const onInviteTeammateClick = () => {
    if (company) {
      openInviteUserModal(company, pagesWithAvailableSeats);
    }
  };

  const humanizedIntegrationNames = {
    default: 'Webhook',
    mailchimp: 'Mailchimp',
    zapier: 'Zapier',
    google_spreadsheet: 'Google Sheets',
    klaviyo: 'Klaviyo',
    salesforce: 'SalesForce',
    sendlane: 'Sendlane',
    shopify: 'Shopify',
  };

  type Crm = {
    integrationType?: string;
    active?: boolean;
  };

  const [crms, setCrms] = useState<Crm[] | []>([]);

  const getUsedCRMS = async () => {
    if (facebookPage) {
      const res = await retriveAddedCrms(facebookPage.id);

      const CRMList = Object.keys(humanizedIntegrationNames).map(
        integration => ({
          integrationType: integration,
          active: res?.crmConnections.includes(integration),
        }),
      );

      setCrms(CRMList);
    }
  };

  const retriveInvites = async () => {
    if (company?.id) {
      await retrieveAll(company.id);
    }
  };

  useEffect(() => {
    getUsedCRMS();
    getAutomationCounters();
    retriveInvites();
  }, [facebookPage?.id]);

  useEffect(() => {
    if (facebookPage?.id) {
      getAccounts(facebookPage.id);
    }
  }, [facebookPage?.id, getAccounts]);

  const automationCircle = (type, count) => (
    <Tooltip
      key={uniqueId()}
      anchorElement={
        <div className={css.circleContainer}>
          <div>
            <Circle color={type === 'Active' ? '#52C41A' : colors.grayLight} />
          </div>
          <div>{count}</div>
        </div>
      }
      css={{
        container: css.tooltipContainer,
        content: css.tooltipContent,
      }}
      withoutLine
      useHover
    >
      <div className={css.tooltipText}>{type} Automations</div>
    </Tooltip>
  );

  const isShopify = useSelector(selectIsShopifyContext);

  const actions: ActionProps[] = [
    {
      buttonName: 'Install X-Ray Pixel',
      icon: <XRayIcon className={css.channelIcon} />,
      onClick: () => history.push(BotBuilderRoutes.xRay.getLink({ chatbotId })),

      hide: facebookPage?.getemailsActivated,
      buttonClassName: css.xrayActionButton,
    },
    {
      buttonName: !facebookPage?.emailActivated ? 'Connect Email' : '',
      icon: <MailFilled className={css.channelIcon} />,
      onClick: () => {
        if (!accounts.length) {
          openActivateEmailAccountModal();
          return;
        }
        navigateTo('email');
      },
      component: (
        <div className={css.emailAddress} onClick={() => navigateTo('email')}>
          {accounts[0]?.emailAddress}
        </div>
      ),
      disabled: !facebookPage?.getemailsActivated,
    },
    {
      buttonName: 'Connect CRM',
      icon: <AppstoreFilled className={css.channelIcon} />,
      onClick: () => navigateTo('native-integrations'),
      component: crms && (
        <div className={css.crmsBlock}>
          {crms.map(crm => (
            <Tooltip
              key={uniqueId()}
              anchorElement={
                <div
                  onClick={() =>
                    navigateTo(
                      'native-integrations',
                      !crm.active && crm.integrationType,
                    )
                  }
                >
                  <IntegrationIcon
                    integrationType={crm.integrationType}
                    isActive={crm.active}
                  />
                </div>
              }
              css={{
                container: css.tooltipContainer,
                content: css.tooltipContent,
              }}
              withoutLine
              useHover
            >
              <div className={css.tooltipText}>
                {humanizedIntegrationNames[crm.integrationType]}
                {' is '}
                {crm.active ? 'connected' : 'not connected'}
              </div>
            </Tooltip>
          ))}
        </div>
      ),
      disabled: !facebookPage?.getemailsActivated,
      hide: isShopify,
    },
    {
      buttonName: adAccount?.facebookPageAdAccounts?.length
        ? 'Manage Ad Accounts'
        : 'Connect Ad Accounts',
      icon: <NotificationOutlined className={css.channelIcon} />,
      onClick: () => {
        history.push(BotBuilderRoutes.integrations.getLink({ chatbotId }), {
          kind: 'facebook',
        });
      },
      component: (
        <FacebookFilled
          style={{
            fontSize: '24px',
            color: adAccount?.facebookPageAdAccounts?.length
              ? '#415DAE'
              : '#8EA1B2',
          }}
        />
      ),
      disabled: !facebookPage?.getemailsActivated,
      hide: isShopify,
    },
    {
      buttonName: 'Add Conversion URL(s)',
      icon: <LinkOutlined className={css.channelIcon} />,
      onClick: () => openConversionURLModal(),
      disabled: !facebookPage?.getemailsActivated,
      hide: isShopify,
    },
    {
      buttonName: hasAutomations ? 'Go to Automations' : 'Create Automation',
      icon: <ConditionsIcon className={css.channelIcon} color="#bdc8d4" />,
      onClick: () => {
        history.push(BotBuilderRoutes.automationHome.getLink({ chatbotId }));
      },
      component: hasAutomations && (
        <div className={css.automationsRow}>
          <div>
            {automationCounters.active > 0 &&
              automationCircle('Active', automationCounters.active)}
          </div>
          <div>
            {automationCounters.total > 0 &&
              automationCounters.total !== automationCounters.active &&
              automationCircle(
                'Inactive',
                automationCounters.total - automationCounters.active,
              )}
          </div>
        </div>
      ),
      disabled: !facebookPage?.getemailsActivated,
      hide: isShopify,
    },
    {
      buttonName: 'Add Teammates',
      icon: <TeamOutlined className={css.channelIcon} />,
      onClick: () => onInviteTeammateClick(),
      component: crms && (
        <div className={css.crmsBlock}>
          {activeAndPendingUsers.map(
            (user, index) =>
              index < 5 && (
                <Tooltip
                  key={uniqueId()}
                  anchorElement={
                    <UserFilled
                      className={classNames({
                        [css.activeUserIcon]: !user.pending,
                      })}
                    />
                  }
                  css={{
                    container: css.tooltipContainer,
                    content: css.tooltipContent,
                  }}
                  withoutLine
                  useHover
                >
                  <div className={css.tooltipText}>
                    {user.pending
                      ? `${user.email} has not accepted your invite`
                      : user.email}
                  </div>
                </Tooltip>
              ),
          )}
          {activeAndPendingUsers.length > 5 && (
            <span style={{ marginLeft: '-12px' }}>...</span>
          )}
        </div>
      ),
      hide: isShopify,
    },

    {
      buttonName: facebookPage?.subscription ? 'Upgrade Plan' : 'Choose Plan',
      icon: <CrownOutlined className={css.channelIcon} />,
      onClick: () => choosePlan(),
      hide:
        isShopify ||
        facebookPage?.subscription?.planSubtypes.includes(
          PlanSubtypes.teamCustom,
        ),
      component: facebookPage?.subscription && (
        <div>
          Your Plan: <span style={{ fontWeight: 500 }}>{currentPlanName}</span>
        </div>
      ),
      disabled: !facebookPage?.getemailsActivated,
    },
  ];

  const filteredActions = actions.filter(action => !action.hide);

  return (
    <div className={css.container}>
      <Heading size={2} className={css.heading}>
        Activation steps
      </Heading>
      {filteredActions.map(action => (
        <ActivationAction
          key={uniqueId()}
          icon={action.icon}
          onClick={action.onClick}
          buttonName={action.buttonName}
          buttonClassName={action.buttonClassName}
          component={action.component}
          disabled={action.disabled}
          chatbotId={chatbotId}
        />
      ))}
      <div className={css.refer}>
        <div>Refer & Get Paid Every Month</div>
        <PrimaryButton
          className={classNames(css.referButton)}
          color="transparent"
          onClick={() => history.push(SettingsRoutes.affiliates.getLink())}
        >
          Learn More
        </PrimaryButton>
      </div>
      {isEligibleForDemo && (
        <div className={css.footer}>
          <div>Talk to an expert to take advantage of all our features</div>
          <BookDemoButton className={css.bookDemo} utm={IN_APP_DEMO_UTM} />
        </div>
      )}
    </div>
  );
};
